import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { isPasswordRecoveryEmailNotFoundError } from '../../util/errors';
import { propTypes } from '../../util/types';

import {
    Modal,
    Form,
    FieldTextInput,
    PrimaryButton,
    InlineTextButton,
    IconKeys,
    IconEmailSent,
    IconCheckmark,
    IconArrowHead
} from '../';

import {
    recoverPassword,
    retypePasswordRecoveryEmail,
    clearPasswordRecoveryError,
} from '../../containers/PasswordRecoveryPage/PasswordRecoveryPage.duck';

import css from './ForgetPasswordModal.module.css';

const ForgetPasswordModalComponent = props => {
    const {
        isOpen,
        onClose,
        onManageDisableScrolling,
        intl,
        recoveryError,
        recoveryInProgress,
        passwordRequested,
        submittedEmail,
        initialEmail,
        onSubmitEmail,
        onRetypeEmail,
        onClearError
    } = props;

    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [resendAttempted, setResendAttempted] = useState(false);

    // Determine current state
    const isEmailSent = passwordRequested || submittedEmail;
    const isSuccessState = showSuccessMessage;

    // Use submittedEmail if available, otherwise use initialEmail
    const displayEmail = submittedEmail || initialEmail;

    // Handle form submission
    const handleSubmitEmail = (values) => {
        onSubmitEmail(values.email);
    };

    // Handle resend email
    const handleResendEmail = () => {
        setResendAttempted(true);
        onSubmitEmail(displayEmail);
    };

    // Handle back to login
    const handleBackToLogin = () => {
        onRetypeEmail();
        setShowSuccessMessage(false);
        setResendAttempted(false);
        onClose();
    };

    // Clear error when modal opens and handle success state
    React.useEffect(() => {
        if (isOpen) {
            if (recoveryError) {
                onClearError();
            }
            setShowSuccessMessage(false);
            setResendAttempted(false);
        }
    }, [isOpen, onClearError]);

    // Show success message after successful resend
    React.useEffect(() => {
        if (resendAttempted && !recoveryInProgress && !recoveryError && displayEmail) {
            setShowSuccessMessage(true);
            // Hide success message after 3 seconds
            setTimeout(() => setShowSuccessMessage(false), 3000);
        }
    }, [resendAttempted, recoveryInProgress, recoveryError, displayEmail]);

    // Handle different types of recovery errors
    let customErrorText = null;
    if (recoveryError) {
        if (isPasswordRecoveryEmailNotFoundError(recoveryError)) {
            customErrorText = intl.formatMessage({ id: 'ForgetPasswordModal.emailNotFound' });
        } else {
            // Generic error message for other types of errors
            customErrorText = recoveryError.message || intl.formatMessage({ id: 'ForgetPasswordModal.genericError' });
        }
    }

    // Render initial form state
    const renderInitialForm = () => (
        <div className={css.content}>
            {/* <IconKeys className={css.modalIcon} /> */}
            <div className={css.modalIcon}>
                <i className='ri-lock-password-line'></i>
            </div>
            <h1 className={css.modalTitle}>
                <FormattedMessage id="ForgetPasswordModal.forgotPasswordTitle" />
            </h1>
            <p className={css.modalMessage}>
                <FormattedMessage id="ForgetPasswordModal.forgotPasswordMessage" />
            </p>

            <FinalForm
                onSubmit={handleSubmitEmail}
                render={({ handleSubmit, invalid, submitting }) => {
                    const emailLabel = intl.formatMessage({
                        id: 'ForgetPasswordModal.emailLabel',
                    });
                    const emailPlaceholder = intl.formatMessage({
                        id: 'ForgetPasswordModal.emailPlaceholder',
                    });
                    const emailRequiredMessage = intl.formatMessage({
                        id: 'ForgetPasswordModal.emailRequired',
                    });
                    const emailInvalidMessage = intl.formatMessage({
                        id: 'ForgetPasswordModal.emailInvalid',
                    });

                    const emailRequired = validators.required(emailRequiredMessage);
                    const emailValid = validators.emailFormatValid(emailInvalidMessage);

                    return (
                        <Form className={css.form} onSubmit={handleSubmit}>
                            <FieldTextInput
                                className={css.emailInput}
                                type="email"
                                name="email"
                                id="forgetPasswordEmail"
                                autoComplete="email"
                                label={emailLabel}
                                placeholder={emailPlaceholder}
                                validate={validators.composeValidators(emailRequired, emailValid)}
                                customErrorText={customErrorText}
                                // variant="email"
                                iconLeft={<i className='ri-mail-line'></i>}
                            />

                            <div className={css.buttonWrapper}>
                                <PrimaryButton
                                    type="submit"
                                    inProgress={recoveryInProgress}
                                    disabled={invalid || recoveryInProgress}
                                    className={css.sendButton}
                                >
                                    <FormattedMessage id="ForgetPasswordModal.sendResetLink" />
                                </PrimaryButton>
                            </div>
                        </Form>
                    );
                }}
            />

            <div>
                <InlineTextButton className={css.backToLoginButton} onClick={handleBackToLogin}>
                    <i className='ri-arrow-left-line'></i>
                    <FormattedMessage id="ForgetPasswordModal.backToLogin" />
                </InlineTextButton>
            </div>
        </div>
    );

    // Render email sent state
    const renderEmailSent = () => (
        <div className={css.content}>
            <div className={css.modalIcon}>
                <i className='ri-lock-password-line'></i>
            </div>
            <h1 className={css.modalTitle}>
                <FormattedMessage id="ForgetPasswordModal.emailSentTitle" />
            </h1>
            <p className={css.modalMessage}>
                <FormattedMessage id="ForgetPasswordModal.emailSentMessage" />
            </p>

            <div className={css.emailDisplay}><i className='ri-mail-line'></i>
                <span className={css.emailText}>{displayEmail}</span>
            </div>

            {customErrorText && (
                <div className={css.errorMessage}>
                    <span>{customErrorText}</span>
                </div>
            )}

            {isSuccessState && !customErrorText && !recoveryInProgress && (
                <div className={css.successMessage}>
                    <i className='ri-checkbox-circle-fill'></i>
                    <div>
                        <p className={css.successTitle}>
                            <FormattedMessage id="ForgetPasswordModal.resetLinkSentAgainTitle" />
                        </p>

                        <span className={css.successMessageDetails}>
                            <FormattedMessage id="ForgetPasswordModal.resetLinkSentAgain" />
                        </span>
                    </div>
                </div>
            )}

            <div className={css.buttonWrapper}>
                <PrimaryButton
                    onClick={handleResendEmail}
                    inProgress={recoveryInProgress}
                    disabled={recoveryInProgress}
                    className={css.resendButton}
                >
                    <FormattedMessage id="ForgetPasswordModal.resendResetLink" />
                </PrimaryButton>

            </div>
            <InlineTextButton className={css.backToLoginButton} onClick={handleBackToLogin}>
                <i className='ri-arrow-left-line'></i>
                <FormattedMessage id="ForgetPasswordModal.backToLogin" />
            </InlineTextButton>
        </div>
    );

    return (
        <Modal
            id="ForgetPasswordModal"
            isOpen={isOpen}
            onClose={onClose}
            onManageDisableScrolling={onManageDisableScrolling}
            usePortal
            containerClassName={css.modalContainer}
            contentClassName={css.modalContent}
            className={css.modalWrapper}
        >
            {isEmailSent ? renderEmailSent() : renderInitialForm()}
        </Modal>
    );
};

ForgetPasswordModalComponent.defaultProps = {
    recoveryError: null,
    submittedEmail: null,
    initialEmail: null,
};

ForgetPasswordModalComponent.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onManageDisableScrolling: PropTypes.func.isRequired,
    recoveryError: propTypes.error,
    recoveryInProgress: PropTypes.bool.isRequired,
    passwordRequested: PropTypes.bool.isRequired,
    submittedEmail: PropTypes.string,
    initialEmail: PropTypes.string,
    onSubmitEmail: PropTypes.func.isRequired,
    onRetypeEmail: PropTypes.func.isRequired,
    onClearError: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
};

const mapStateToProps = state => {
    const {
        submittedEmail,
        initialEmail,
        recoveryError,
        recoveryInProgress,
        passwordRequested,
    } = state.PasswordRecoveryPage;
    return {
        submittedEmail,
        initialEmail,
        recoveryError,
        recoveryInProgress,
        passwordRequested,
    };
};

const mapDispatchToProps = dispatch => ({
    onSubmitEmail: email => dispatch(recoverPassword(email)),
    onRetypeEmail: () => dispatch(retypePasswordRecoveryEmail()),
    onClearError: () => dispatch(clearPasswordRecoveryError()),
});

const ForgetPasswordModal = compose(
    connect(mapStateToProps, mapDispatchToProps),
    injectIntl
)(ForgetPasswordModalComponent);

export default ForgetPasswordModal;
